import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import StarRating from '@/components/ui/star-rating';
import { toast } from 'sonner';
import { selectSelectedEntity, selectEditorInteractionState, updateReview } from '@/store/slices/editorSlice';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

interface ReviewEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ReviewData {
  id: string;
  userId: string;
  username: string;
  drinkId: string;
  drinkName: string;
  brandId: string;
  brandName: string;
  rating: number;
  reviewText: string;
  isPublic: boolean;
  locationId?: string;
  locationName?: string;
  date: number; // Timestamp in milliseconds
}

export const ReviewEditDialog: React.FC<ReviewEditDialogProps> = ({ 
  open, 
  onOpenChange 
}) => {
  const dispatch = useAppDispatch();
  const selectedEntity = useAppSelector(selectSelectedEntity);
  const interactionState = useAppSelector(selectEditorInteractionState);
  
  const [reviewData, setReviewData] = useState<ReviewData | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Form state
  const [reviewText, setReviewText] = useState('');
  const [rating, setRating] = useState(0);
  const [isPublic, setIsPublic] = useState(true);
  
  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setHasFetched(false);
    }
  }, [open]);
  
  // Fetch the review data when the dialog opens
  useEffect(() => {
    const fetchReviewData = async () => {
      if (!selectedEntity || selectedEntity.type !== 'review' || hasFetched) {
        return;
      }
      
      setLoading(true);
      setError(null);
      
      try {
        const reviewRef = doc(db, 'reviews', selectedEntity.id);
        const reviewSnap = await getDoc(reviewRef);
        
        if (!reviewSnap.exists()) {
          setError('Review not found');
          setLoading(false);
          return;
        }
        
        const data = reviewSnap.data() as any;
        const review: ReviewData = {
          ...data,
          id: reviewSnap.id,
          date: (() => {
            const dateValue = data.date;
            // Handle Firestore timestamps (they have a toDate method)
            if (dateValue && typeof dateValue === 'object' && 'toDate' in dateValue) {
              return dateValue.toDate().getTime();
            }
            // Handle JavaScript Date objects
            if (dateValue && dateValue instanceof Date) {
              return dateValue.getTime();
            }
            // Handle numeric timestamps (milliseconds since epoch)
            if (typeof dateValue === 'number') {
              return dateValue;
            }
            // Fallback to current timestamp
            return Date.now();
          })()
        };
        
        setReviewData(review);
        setReviewText(review.reviewText || '');
        setRating(review.rating);
        setIsPublic(review.isPublic);
        
        // Mark as fetched for this session to prevent duplicate fetches
        setHasFetched(true);
      } catch (err) {
        console.error('Error fetching review:', err);
        setError('Error fetching review data');
      } finally {
        setLoading(false);
      }
    };
    
    // Only fetch when dialog is open and we're in editing state and haven't fetched yet
    if (open && interactionState === 'editing' && selectedEntity?.type === 'review' && !hasFetched) {
      fetchReviewData();
    }
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, interactionState, hasFetched]);
  
  const handleSave = async () => {
    if (!reviewData) return;
    
    setIsSaving(true);
    
    try {
      await dispatch(updateReview({
        id: reviewData.id,
        data: {
          reviewText,
          rating,
          isPublic
        }
      })).unwrap();
      
      toast.success('Review updated successfully');
      onOpenChange(false);
    } catch (err) {
      console.error('Error updating review:', err);
      toast.error('Failed to update review');
    } finally {
      setIsSaving(false);
    }
  };
  
  if (!open) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit Review</DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : error ? (
          <div className="text-destructive py-4">{error}</div>
        ) : reviewData ? (
          <div className="space-y-4 py-2">
            <div>
              <h3 className="text-lg font-semibold">{reviewData.drinkName}</h3>
              <p className="text-sm text-muted-foreground">{reviewData.brandName}</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="rating">Rating</Label>
              <StarRating
                value={rating}
                size="lg"
                readOnly
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reviewText">Review Text</Label>
              <Textarea
                id="reviewText"
                placeholder="Write your review here..."
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                className="min-h-[120px]"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Visibility</Label>
              <RadioGroup 
                value={isPublic ? 'public' : 'private'} 
                onValueChange={(value) => setIsPublic(value === 'public')}
                className="flex flex-col space-y-1"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="public" id="public" />
                  <Label htmlFor="public" className="font-normal cursor-pointer">Public - Visible to everyone</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="private" id="private" />
                  <Label htmlFor="private" className="font-normal cursor-pointer">Private - Only contributes to rating</Label>
                </div>
              </RadioGroup>
            </div>
            
            <div className="text-xs text-muted-foreground">
              <p>Review by: {reviewData.username}</p>
              <p>Date: {new Date(reviewData.date).toLocaleString()}</p>
              {reviewData.locationName && (
                <p>Location: {reviewData.locationName}</p>
              )}
            </div>
          </div>
        ) : null}
        
        <DialogFooter>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Cancel
          </Button>
          
          <Button 
            type="button" 
            onClick={handleSave} 
            disabled={loading || error !== null || isSaving || !reviewData}
            className="gap-1"
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}; 